import type {
  FeatureAnnouncement,
  FeatureTourReadinessSource,
} from '../models';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isDate(value: unknown): boolean {
  return isText(value) && Number.isFinite(Date.parse(value));
}

function isOptionalText(value: unknown): boolean {
  return value === undefined || isText(value);
}

function isOptionalBoolean(value: unknown): boolean {
  return value === undefined || typeof value === 'boolean';
}

function isRoute(value: unknown): boolean {
  return (
    value === undefined ||
    (isText(value) && value.startsWith('/') && !value.startsWith('//'))
  );
}

function isSelector(value: unknown, ownerDocument: Document): boolean {
  if (!isText(value)) {
    return false;
  }

  try {
    ownerDocument.createDocumentFragment().querySelector(value);

    return true;
  } catch {
    return false;
  }
}

function isTourStep(value: unknown, ownerDocument: Document): boolean {
  return (
    isRecord(value) &&
    isSelector(value['selector'], ownerDocument) &&
    isText(value['title']) &&
    isText(value['description']) &&
    (value['side'] === undefined ||
      (typeof value['side'] === 'string' &&
        ['top', 'right', 'bottom', 'left'].includes(value['side']))) &&
    (value['align'] === undefined ||
      (typeof value['align'] === 'string' &&
        ['start', 'center', 'end'].includes(value['align']))) &&
    isOptionalBoolean(value['optional']) &&
    (value['advanceOn'] === undefined || value['advanceOn'] === 'anchor-click')
  );
}

const readinessSourcePathKeys = [
  'loadingPaths',
  'loadedPaths',
  'errorPaths',
  'dataPaths',
];

const isReadinessSource = (
  value: unknown,
): value is FeatureTourReadinessSource | undefined => {
  if (value === undefined) {
    return true;
  }

  if (!isRecord(value) || !isText(value['stateKey'])) {
    return false;
  }

  return readinessSourcePathKeys.every((key) => {
    const paths = value[key];

    return Array.isArray(paths) && paths.length > 0 && paths.every(isText);
  });
};

function isReadiness(value: unknown): boolean {
  if (value === undefined) {
    return true;
  }

  if (
    !isRecord(value) ||
    !isRoute(value['routeKey']) ||
    value['selectorKey'] !== undefined ||
    !isReadinessSource(value['source']) ||
    !isOptionalBoolean(value['waitForNetworkIdle'])
  ) {
    return false;
  }

  const { graphql: requirements } = value;

  return (
    requirements === undefined ||
    (Array.isArray(requirements) &&
      requirements.every(
        (requirement: unknown) =>
          isRecord(requirement) &&
          isText(requirement['clientName']) &&
          isText(requirement['fieldName']) &&
          isOptionalText(requirement['requiredPath']) &&
          isOptionalBoolean(requirement['requireNonEmpty']),
      ))
  );
}

export function isFeatureAnnouncement(
  value: unknown,
  ownerDocument: Document,
): value is FeatureAnnouncement {
  if (
    !isRecord(value) ||
    !isText(value['featureKey']) ||
    !isText(value['title']) ||
    !isText(value['description']) ||
    (value['changeType'] !== 'new' && value['changeType'] !== 'updated') ||
    !isDate(value['publishedAt']) ||
    (value['expiryDate'] !== undefined && !isDate(value['expiryDate'])) ||
    typeof value['hasTour'] !== 'boolean' ||
    !isOptionalText(value['ctaLabel']) ||
    (value['hasTour'] && !isText(value['ctaLabel'])) ||
    !isRoute(value['route']) ||
    !isReadiness(value['readiness']) ||
    !['sidebarTitle', 'navBadgeTarget', 'badgeTooltip'].every((key) =>
      isOptionalText(value[key]),
    )
  ) {
    return false;
  }

  const {
    sidebarHighlights: highlights,
    badgeTargets,
    tourSteps: steps,
  } = value;

  return (
    (highlights === undefined ||
      (Array.isArray(highlights) &&
        highlights.every(
          (highlight: unknown) =>
            isRecord(highlight) &&
            isText(highlight['title']) &&
            isText(highlight['description']),
        ))) &&
    (badgeTargets === undefined ||
      (Array.isArray(badgeTargets) &&
        badgeTargets.every(
          (target: unknown) =>
            isRecord(target) &&
            isSelector(target['selector'], ownerDocument) &&
            isOptionalText(target['tooltip']),
        ))) &&
    (steps === undefined ||
      (Array.isArray(steps) &&
        steps.every((step: unknown) => isTourStep(step, ownerDocument)))) &&
    (!value['hasTour'] || (Array.isArray(steps) && steps.length > 0))
  );
}
